import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Table, 
  TableHeader, 
  TableColumn, 
  TableBody, 
  TableRow, 
  TableCell, 
  Button,
  Chip,
  Pagination,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Image
} from "@nextui-org/react";
import { Search, Edit, Eye, Trash2, Calendar, ArrowLeft } from "lucide-react";
import axios from "axios";
import { AuthContext } from "../../Context/AuthContext";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config/apiConfig";

// Default categories with subcategories structure (same as Blog.jsx)
const defaultCategories = [
  { value: "Travel Tips", label: "Travel Tips", hexColor: "#4F46E5" },
  { value: "Travel", label: "Travel", hexColor: "#0EA5E9" },
  { value: "Projects", label: "Projects", hexColor: "#10B981" },
  { value: "Event", label: "Event", hexColor: "#F59E0B" },
  { value: "Sport", label: "Sport", hexColor: "#EF4444" },
  { value: "Music", label: "Music", hexColor: "#8B5CF6" },
  { value: "Art", label: "Art", hexColor: "#EC4899" },
  { value: "Wellness", label: "Wellness", hexColor: "#06B6D4" },
  { value: "Lifestyle", label: "Lifestyle", hexColor: "#14B8A6" },
  { value: "Productivity", label: "Productivity", hexColor: "#F97316" },
  { value: "Seasonal", label: "Seasonal", hexColor: "#D946EF" },
  { value: "Stories", label: "Stories", hexColor: "#8B5CF6" },
  { value: "Technology", label: "Technology", hexColor: "#3B82F6" },
  { value: "Food", label: "Food", hexColor: "#10B981" },
  { value: "Business", label: "Business", hexColor: "#0EA5E9" },
  { value: "Education", label: "Education", hexColor: "#8B5CF6" },
  { value: "Finance", label: "Finance", hexColor: "#10B981" },
  { value: "Health", label: "Health", hexColor: "#EF4444" },
  { value: "Photography", label: "Photography", hexColor: "#8B5CF6" },
  { value: "Books", label: "Books", hexColor: "#F59E0B" },
];

// Default subcategories for each main category
const defaultSubcategories = {
  "Travel Tips": [
    { value: "travel-tips-packing", label: "Packing Tips" },
    { value: "travel-tips-budget", label: "Budget Travel Tips" },
    { value: "travel-tips-safety", label: "Safety Tips" },
    { value: "travel-tips-planning", label: "Trip Planning" },
    { value: "travel-tips-accommodation", label: "Accommodation Tips" },
    { value: "travel-tips-transportation", label: "Transportation Tips" },
    { value: "travel-tips-food", label: "Food & Dining Tips" },
  ],
  "Travel": [
    { value: "travel-adventure", label: "Adventure Travel" },
    { value: "travel-budget", label: "Budget Travel" },
    { value: "travel-luxury", label: "Luxury Travel" },
    { value: "travel-solo", label: "Solo Travel" },
    { value: "travel-family", label: "Family Travel" },
    { value: "travel-backpacking", label: "Backpacking" },
    { value: "travel-road-trip", label: "Road Trips" },
    { value: "travel-beach", label: "Beach Destinations" },
    { value: "travel-mountain", label: "Mountain Getaways" },
    { value: "travel-cultural", label: "Cultural Immersion" },
    { value: "travel-food", label: "Food & Culinary Travel" },
    { value: "travel-wellness", label: "Wellness Retreats" },
  ],
  "Projects": [
    { value: "projects-diy", label: "DIY Projects" },
    { value: "projects-home-improvement", label: "Home Improvement" },
    { value: "projects-gardening", label: "Gardening Projects" },
    { value: "projects-crafts", label: "Crafts & Art Projects" },
    { value: "projects-tech", label: "Tech Projects" },
    { value: "projects-woodworking", label: "Woodworking" },
    { value: "projects-renovation", label: "Home Renovation" },
    { value: "projects-upcycling", label: "Upcycling Projects" },
  ],
  "Event": [
    { value: "event-weddings", label: "Weddings" },
    { value: "event-birthdays", label: "Birthdays" },
    { value: "event-Art", label: "Arts & Culture" },
    { value: "event-corporate", label: "Corporate" },
    { value: "event-conferences", label: "Conferences" },
    { value: "event-festivals", label: "Festivals" },
    { value: "event-Music & Nightlife", label: "Music & Nightlife" },
    { value: "event-sports", label: "Sports" },
    { value: "event-charity", label: "Charity" },
    { value: "event-exhibitions", label: "Exhibitions" },
    { value: "event-launches", label: "Product Launches" },
    { value: "event-fashion", label: "Fashion" },
    { value: "event-Family & Lifestyle", label: "Family & Lifestyle" },
  ],
  "Sport": [
    { value: "sport-football", label: "Football" },
    { value: "sport-basketball", label: "Basketball" },
    { value: "sport-tennis", label: "Tennis" },
    { value: "sport-golf", label: "Golf" },
    { value: "sport-cricket", label: "Cricket" },
    { value: "sport-rugby", label: "Rugby" },
    { value: "sport-swimming", label: "Swimming" },
    { value: "sport-running", label: "Running" },
    { value: "sport-cycling", label: "Cycling" },
    { value: "sport-yoga", label: "Yoga" },
    { value: "sport-gym", label: "Gym & Fitness" },
    { value: "sport-extreme", label: "Extreme Sports" },
    { value: "sport-esports", label: "eSports" },
  ],
  // "Music": [
  //   { value: "music-rock", label: "Rock" },
  //   { value: "music-pop", label: "Pop" },
  //   { value: "music-hiphop", label: "Hip Hop" },
  //   { value: "music-jazz", label: "Jazz" },
  //   { value: "music-classical", label: "Classical" },
  //   { value: "music-electronic", label: "Electronic" },
  //   { value: "music-rnb", label: "R&B" },
  //   { value: "music-country", label: "Country" },
  //   { value: "music-indie", label: "Indie" },
  //   { value: "music-metal", label: "Metal" },
  //   { value: "music-folk", label: "Folk" },
  //   { value: "music-reggae", label: "Reggae" },
  //   { value: "music-blues", label: "Blues" },
  //   { value: "music-instruments", label: "Musical Instruments" },
  //   { value: "music-production", label: "Music Production" },
  // ],
  // "Art": [
  //   { value: "art-painting", label: "Painting" },
  //   { value: "art-drawing", label: "Drawing" },
  //   { value: "art-sculpture", label: "Sculpture" },
  //   { value: "art-digital", label: "Digital Art" },
  //   { value: "art-photography", label: "Photography" },
  //   { value: "art-illustration", label: "Illustration" },
  //   { value: "art-graffiti", label: "Graffiti & Street Art" },
  //   { value: "art-ceramics", label: "Ceramics" },
  //   { value: "art-printmaking", label: "Printmaking" },
  //   { value: "art-collage", label: "Collage" },
  //   { value: "art-mixed-media", label: "Mixed Media" },
  //   { value: "art-architecture", label: "Architecture" },
  //   { value: "art-fashion", label: "Fashion Design" },
  //   { value: "art-graphic", label: "Graphic Design" },
  // ],
  "Wellness": [
    { value: "wellness-meditation", label: "Meditation" },
    { value: "wellness-yoga", label: "Yoga" },
    { value: "wellness-nutrition", label: "Nutrition" },
    { value: "wellness-fitness", label: "Fitness" },
    { value: "wellness-mental-health", label: "Mental Health" },
    { value: "wellness-sleep", label: "Sleep & Recovery" },
    { value: "wellness-holistic", label: "Holistic Health" },
    { value: "wellness-spa", label: "Spa & Relaxation" },
    { value: "wellness-ayurveda", label: "Ayurveda" },
    { value: "wellness-acupuncture", label: "Acupuncture" },
    { value: "wellness-massage", label: "Massage Therapy" },
    { value: "wellness-diet", label: "Diet & Detox" },
    { value: "wellness-mindfulness", label: "Mindfulness" },
  ],
  "Lifestyle": [
    { value: "lifestyle-fashion", label: "Fashion & Style" },
    { value: "lifestyle-beauty", label: "Beauty" },
    { value: "lifestyle-home", label: "Home & Decor" },
    { value: "lifestyle-relationships", label: "Relationships" },
    { value: "lifestyle-parenting", label: "Parenting" },
    { value: "lifestyle-pets", label: "Pets" },
    { value: "lifestyle-minimalism", label: "Minimalism" },
    { value: "lifestyle-sustainable", label: "Sustainable Living" },
    { value: "lifestyle-hobbies", label: "Hobbies" },
    { value: "lifestyle-entertainment", label: "Entertainment" },
    { value: "lifestyle-social", label: "Social Life" },
    { value: "lifestyle-personal-growth", label: "Personal Growth" },
  ],
  "Productivity": [
    { value: "productivity-time-management", label: "Time Management" },
    { value: "productivity-goal-setting", label: "Goal Setting" },
    { value: "productivity-habits", label: "Habit Building" },
    { value: "productivity-focus", label: "Focus & Concentration" },
    { value: "productivity-tools", label: "Productivity Tools" },
    { value: "productivity-work-life", label: "Work-Life Balance" },
    { value: "productivity-organization", label: "Organization" },
    { value: "productivity-planning", label: "Planning Systems" },
    { value: "productivity-motivation", label: "Motivation" },
    { value: "productivity-delegation", label: "Delegation" },
    { value: "productivity-remote-work", label: "Remote Work" },
    { value: "productivity-learning", label: "Learning Efficiency" },
  ],
  "Seasonal": [
    { value: "seasonal-christmas", label: "Christmas" },
    { value: "seasonal-halloween", label: "Halloween" },
    { value: "seasonal-easter", label: "Easter" },
    { value: "seasonal-new-year", label: "New Year" },
    { value: "seasonal-valentines", label: "Valentine's Day" },
    { value: "seasonal-thanksgiving", label: "Thanksgiving" },
    { value: "seasonal-summer", label: "Summer Activities" },
    { value: "seasonal-winter", label: "Winter Activities" },
    { value: "seasonal-spring", label: "Spring Activities" },
    { value: "seasonal-autumn", label: "Autumn Activities" },
    { value: "seasonal-holidays", label: "Holiday Planning" },
    { value: "seasonal-festivals", label: "Seasonal Festivals" },
  ],
  "Stories": [
    { value: "stories-personal", label: "Personal Stories" },
    { value: "stories-travel", label: "Travel Stories" },
    { value: "stories-fiction", label: "Fiction" },
    { value: "stories-inspirational", label: "Inspirational Stories" },
    { value: "stories-humorous", label: "Humorous Stories" },
    { value: "stories-mystery", label: "Mystery" },
    { value: "stories-romance", label: "Romance" },
    { value: "stories-adventure", label: "Adventure Stories" },
    { value: "stories-historical", label: "Historical Stories" },
    { value: "stories-fantasy", label: "Fantasy" },
    { value: "stories-sci-fi", label: "Science Fiction" },
    { value: "stories-interviews", label: "Interviews" },
  ],
  "Technology": [
    { value: "tech-ai", label: "Artificial Intelligence" },
    { value: "tech-web", label: "Web Development" },
    { value: "tech-mobile", label: "Mobile Development" },
    { value: "tech-gadgets", label: "Gadgets & Reviews" },
    { value: "tech-security", label: "Cybersecurity" },
    { value: "tech-software", label: "Software Engineering" },
    { value: "tech-blockchain", label: "Blockchain & Crypto" },
    { value: "tech-data-science", label: "Data Science" },
    { value: "tech-cloud", label: "Cloud Computing" },
    { value: "tech-iot", label: "Internet of Things" },
    { value: "tech-gaming", label: "Gaming" },
    { value: "tech-startups", label: "Startups & Innovation" },
    { value: "tech-programming", label: "Programming Languages" },
    { value: "tech-hardware", label: "Hardware" },
    { value: "tech-robotics", label: "Robotics" },
    { value: "tech-vr-ar", label: "VR & AR" },
  ],
  "Food": [
    { value: "food-recipes", label: "Recipes" },
    { value: "food-restaurants", label: "Restaurant Reviews" },
    { value: "food-healthy", label: "Healthy Eating" },
    { value: "food-baking", label: "Baking" },
    { value: "food-vegan", label: "Vegan & Vegetarian" },
    { value: "food-desserts", label: "Desserts" },
    { value: "food-drinks", label: "Drinks & Beverages" },
    { value: "food-cooking-tips", label: "Cooking Tips" },
    { value: "food-meal-prep", label: "Meal Prep" },
    { value: "food-international", label: "International Cuisine" },
    { value: "food-street-food", label: "Street Food" },
    { value: "food-fine-dining", label: "Fine Dining" },
    { value: "food-food-travel", label: "Food Travel" },
  ],
  "Business": [
    { value: "business-entrepreneurship", label: "Entrepreneurship" },
    { value: "business-marketing", label: "Marketing" },
    { value: "business-finance", label: "Finance" },
    { value: "business-management", label: "Management" },
    { value: "business-leadership", label: "Leadership" },
    { value: "business-sales", label: "Sales" },
    { value: "business-investing", label: "Investing" },
    { value: "business-strategy", label: "Business Strategy" },
    { value: "business-startups", label: "Startups" },
    { value: "business-ecommerce", label: "E-commerce" },
  ],
  "Education": [
    { value: "education-learning", label: "Learning Techniques" },
    { value: "education-online", label: "Online Education" },
    { value: "education-career", label: "Career Development" },
    { value: "education-languages", label: "Language Learning" },
    { value: "education-skill-development", label: "Skill Development" },
    { value: "education-study-tips", label: "Study Tips" },
    { value: "education-parenting", label: "Education & Parenting" },
  ],
  "Finance": [
    { value: "finance-investing", label: "Investing" },
    { value: "finance-saving", label: "Saving Money" },
    { value: "finance-budgeting", label: "Budgeting" },
    { value: "finance-stocks", label: "Stock Market" },
    { value: "finance-crypto", label: "Cryptocurrency" },
    { value: "finance-real-estate", label: "Real Estate" },
    { value: "finance-taxes", label: "Tax Planning" },
  ],
  "Health": [
    { value: "health-fitness", label: "Fitness" },
    { value: "health-nutrition", label: "Nutrition" },
    { value: "health-mental", label: "Mental Health" },
    { value: "health-chronic", label: "Chronic Conditions" },
    { value: "health-preventive", label: "Preventive Care" },
    { value: "health-alternative", label: "Alternative Medicine" },
  ],
  "Photography": [
    { value: "photography-portrait", label: "Portrait Photography" },
    { value: "photography-landscape", label: "Landscape Photography" },
    { value: "photography-street", label: "Street Photography" },
    { value: "photography-wildlife", label: "Wildlife Photography" },
    { value: "photography-editing", label: "Photo Editing" },
    { value: "photography-equipment", label: "Camera Equipment" },
  ],
  "Books": [
    { value: "books-fiction", label: "Fiction" },
    { value: "books-nonfiction", label: "Non-Fiction" },
    { value: "books-self-help", label: "Self-Help" },
    { value: "books-reviews", label: "Book Reviews" },
    { value: "books-recommendations", label: "Recommendations" },
    { value: "books-writing", label: "Writing Tips" },
  ],
};

// Helper function to get category hex color
const getCategoryHexColor = (categoryName) => {
  if (!categoryName) return "#6B7280";
  const found = defaultCategories.find(cat => cat.value === categoryName || cat.label === categoryName);
  return found?.hexColor || "#6B7280";
};

// Helper function to get subcategory label
const getSubcategoryLabel = (mainCategory, subcategoryValue) => {
  if (!subcategoryValue || !mainCategory) return "";
  const subcategories = defaultSubcategories[mainCategory] || [];
  const found = subcategories.find(sub => sub.value === subcategoryValue);
  return found?.label || subcategoryValue || "";
};

// Helper function to get category from post
const getPostCategory = (draft) => {
  // Check categories array format first
  if (Array.isArray(draft.categories) && draft.categories.length > 0) {
    return draft.categories[0];
  }
  return draft.category || "Uncategorized";
};

// Helper function to get subcategory from post
const getPostSubcategory = (draft) => {
  // Check categories array format
  if (Array.isArray(draft.categories) && draft.categories.length > 1) {
    return draft.categories[1];
  }
  return draft.subcategory || "";
};

const getBlogsFromResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.response?.data)) return payload.response.data;
  if (Array.isArray(payload?.response?.data?.blogs)) return payload.response.data.blogs;
  if (Array.isArray(payload?.response?.data?.data)) return payload.response.data.data;
  if (Array.isArray(payload?.blogs)) return payload.blogs;
  if (Array.isArray(payload?.drafts)) return payload.drafts;
  return [];
};

const isDraftBlog = (blog) => {
  return (
    blog?.is_published === false ||
    blog?.is_published === 0 ||
    blog?.is_published === "0" ||
    blog?.status === "draft" ||
    blog?.published_at === null
  );
};

const AdminDraftBlogs = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      //console.log('📡 Fetching draft blogs...');
      const response = await axios.get(
        `${API_BASE_URL}/admin/blogs`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      //console.log('📥 Drafts API response:', response.data);
      
      const allDrafts = getBlogsFromResponse(response.data);
      if (!allDrafts.length) {
        console.warn('⚠️ Unexpected drafts response format:', response.data);
      }
      
      //console.log(`✅ Loaded ${allDrafts.length} draft blogs`);
      
      // Filter to show only unpublished blogs (drafts)
      const draftBlogs = allDrafts.filter(isDraftBlog);
      //console.log(`📝 Filtered to ${draftBlogs.length} drafts (is_published: false)`);
      setDrafts(draftBlogs);
      
      // Calculate total pages based on data length
      const itemsPerPage = 10;
      setTotalPages(Math.ceil(allDrafts.length / itemsPerPage) || 1);
      
    } catch (error) {
      console.error("❌ Error fetching drafts:", error);
      console.error("❌ Error response:", error.response?.data);
      toast.error("Failed to load draft blogs");
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, [page, token]);

  const handleEdit = (id) => {
    navigate(`/admin/blog/edit/${id}`);
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await axios.delete(
          `${API_BASE_URL}/admin/blogs/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success("Blog deleted successfully");
        fetchDrafts(); // Refresh list
      } catch (error) {
        toast.error("Failed to delete blog");
      }
    }
  };

  const handlePublish = async (id) => {
    try {
      await axios.post(
        `${API_BASE_URL}/admin/blogs/${id}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Blog published successfully!");
      fetchDrafts(); 
    } catch (error) {
      toast.error("Failed to publish blog");
    }
  };

  const handleView = (draft) => {
    setSelectedDraft(draft);
    onOpen();
  };

  const filteredDrafts = drafts.filter(draft =>
    draft.title?.toLowerCase().includes(search.toLowerCase()) ||
    draft.excerpt?.toLowerCase().includes(search.toLowerCase())
  );

  // View Modal Component
  const renderViewModal = () => (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="3xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[90vh]",
        body: "overflow-y-auto"
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-xl font-bold">{selectedDraft?.title}</h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Chip size="sm" color="warning" variant="flat">
                  Draft
                </Chip>
                <Chip 
                  size="sm" 
                  variant="flat"
                  style={{ 
                    backgroundColor: getCategoryHexColor(getPostCategory(selectedDraft)) + '20',
                    color: getCategoryHexColor(getPostCategory(selectedDraft))
                  }}
                >
                  {getPostCategory(selectedDraft)}
                </Chip>
                {getPostSubcategory(selectedDraft) && (
                  <Chip size="sm" variant="flat" color="secondary">
                    {getSubcategoryLabel(getPostCategory(selectedDraft), getPostSubcategory(selectedDraft))}
                  </Chip>
                )}
              </div>
            </ModalHeader>
            <ModalBody>
              {selectedDraft?.featured_image && (
                <div className="mb-4">
                  <Image
                    src={selectedDraft.featured_image}
                    alt={selectedDraft.title}
                    className="w-full h-64 object-cover rounded-lg"
                    removeWrapper
                  />
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500">Excerpt</h4>
                  <p className="text-gray-700">{selectedDraft?.excerpt || "No excerpt available"}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500">Content</h4>
                  <div 
                    className="prose max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: selectedDraft?.content || "No content" }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500">Created</h4>
                    <p className="text-gray-700">
                      {selectedDraft?.created_at 
                        ? new Date(selectedDraft.created_at).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500">Last Updated</h4>
                    <p className="text-gray-700">
                      {selectedDraft?.updated_at 
                        ? new Date(selectedDraft.updated_at).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onClick={onClose}>
                Close
              </Button>
              <Button 
                color="primary" 
                onClick={() => {
                  onClose();
                  handleEdit(selectedDraft?.id);
                }}
              >
                <Edit size={16} />
                Edit Blog
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8">
        <div className="container xl:px-20 px-6 mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-mainGradiant"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8">
      <div className="container xl:px-20 px-6 mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Draft Events</h1>
            <p className="text-gray-600 mt-1">Manage your unpublished events</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="flat"
              onClick={() => navigate("/admin/blog")}
              startContent={<ArrowLeft size={16} />}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              All Posts
            </Button>
            <Button
              color="primary"
              onClick={() => navigate("/admin/blog/create")}
            >
              + Create New Post
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder="Search drafts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startContent={<Search className="text-gray-400" />}
            className="max-w-md"
          />
        </div>

        {/* Drafts Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <Table aria-label="Draft blogs table">
            <TableHeader>
              <TableColumn>Title</TableColumn>
              <TableColumn>Category</TableColumn>
              <TableColumn>Subcategory</TableColumn>
              <TableColumn>Created</TableColumn>
              <TableColumn>Status</TableColumn>
              <TableColumn>Actions</TableColumn>
            </TableHeader>
            <TableBody emptyContent="No draft blogs found">
              {filteredDrafts.map((draft) => (
                <TableRow key={draft.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{draft.title}</p>
                      <p className="text-sm text-gray-500 truncate max-w-xs">
                        {draft.excerpt || "No excerpt"}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Chip 
                        size="sm" 
                        variant="flat"
                        style={{ 
                          backgroundColor: getCategoryHexColor(getPostCategory(draft)) + '20',
                          color: getCategoryHexColor(getPostCategory(draft))
                        }}
                      >
                        {getPostCategory(draft)}
                      </Chip>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                     
                      {getPostSubcategory(draft) && (
                        <Chip size="sm" variant="flat" color="secondary">
                          {getSubcategoryLabel(getPostCategory(draft), getPostSubcategory(draft))}
                        </Chip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar size={14} />
                      {new Date(draft.created_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="sm" 
                      color="warning" 
                      variant="flat"
                      startContent={<span className="w-2 h-2 bg-yellow-500 rounded-full"></span>}
                    >
                      Draft
                    </Chip>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        onClick={() => handleView(draft)}
                      >
                        <Eye size={16} />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        onClick={() => handleEdit(draft.id)}
                      >
                        <Edit size={16} />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        color="success"
                        onClick={() => handlePublish(draft.id)}
                      >
                        <Eye size={16} />
                        Publish
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        color="danger"
                        onClick={() => handleDelete(draft.id, draft.title)}
                      >
                        <Trash2 size={16} />
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <Pagination
              page={page}
              total={totalPages}
              onChange={setPage}
              color="primary"
            />
          </div>
        )}

        {/* View Modal */}
        {renderViewModal()}
      </div>
    </div>
  );
};

export default AdminDraftBlogs;
